import {
  AfterContentChecked,
  AfterContentInit,
  Component,
  computed,
  contentChildren,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  viewChild,
} from '@angular/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import {
  CellClickEvent,
  ColumnComponent,
  ColumnMenuSettings,
  DataStateChangeEvent,
  FilterableSettings,
  GridComponent,
  GridDataResult,
  GroupableSettings,
  KENDO_GRID,
  ScrollMode,
  SortSettings,
} from '@progress/kendo-angular-grid';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_TEXTBOX } from '@progress/kendo-angular-inputs';
import { KENDO_CARD, KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { PagerSettings } from '@progress/kendo-angular-listview';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { DebounceValueChangesDirective } from '@unity/directives/debounce-value-changes.directive';
import { GridService } from '@unity/services/grid.service';
import { LayoutService } from '@unity/services/layout.service';
import { DefaultGlobalSort, DefaultPageSize } from '@unity/utils/constants';
import { GridifyQuery, SortConfig } from '@unity/utils/types';

@Component({
  selector: 'unity-grid-shared',
  imports: [KENDO_CARD, KENDO_GRID, KENDO_STACKLAYOUT, KENDO_BUTTON, KENDO_TEXTBOX, KENDO_LOADER, DebounceValueChangesDirective],
  templateUrl: './grid-shared.component.html',
  styleUrl: './grid-shared.component.scss',
})
export class GridSharedComponent implements AfterContentChecked, AfterContentInit {
  private readonly layoutService = inject(LayoutService);
  private readonly gridService = inject(GridService);

  private readonly $gridReference = viewChild.required<GridComponent>('grid');
  private readonly $columnsReference = contentChildren(ColumnComponent);

  protected readonly gridWidth = '100%';
  protected readonly menuSettings: ColumnMenuSettings = {
    filter: true,
  };

  protected readonly $orientation = this.layoutService.$orientation;
  protected readonly $groupable = computed(() => this.$isGroupable() && this.$gridData().total > 0);
  protected readonly $pageable = computed(
    () => this.$isPageable() && this.$gridData().total > 0 && this.$gridData().total > this.$defaultPageSize()
  );
  protected readonly $sortable = computed(() => this.$isSortable() && this.$gridData().total > 0);
  protected readonly $scrollable = computed<ScrollMode>(() => (this.$orientation() === 'vertical' ? 'scrollable' : 'none'));

  protected readonly $filterable = computed(() => (this.$isFilterable() ? 'menu' : false));

  protected readonly $searchbarClass = computed(() =>
    this.$orientation() === 'horizontal' ? 'searchbar' : 'searchbar searchbar-vertical'
  );
  protected readonly searchValue = '';

  protected readonly $gridDataQuery = linkedSignal<State>(() => {
    const defaultSort = this.$defaultSort();
    const defaultPageSize = this.$defaultPageSize();
    const defaultFilter = this.$defaultFilter();
    return {
      take: defaultPageSize,
      sort: defaultSort,
      filter: defaultFilter,
    };
  });

  protected readonly $isSearchLoading = linkedSignal(() => !this.$gridData());

  /// Inputs
  public readonly $gridTitle = input.required<string>({ alias: 'gridTitle' });
  public readonly $gridData = input.required<GridDataResult>({ alias: 'gridData' });
  public readonly $gridError = input.required<string | undefined>({ alias: 'gridError' });
  public readonly $gridLoading = input.required<boolean>({ alias: 'gridLoading' });

  public readonly $isGroupable = input<GroupableSettings | boolean>(false, { alias: 'isGroupable' });
  public readonly $isPageable = input<PagerSettings | boolean>(true, { alias: 'isPageable' });
  public readonly $isSortable = input<SortSettings | boolean>(true, { alias: 'isSortable' });
  public readonly $isFilterable = input<FilterableSettings | boolean>(true, { alias: 'isFilterable' });
  public readonly $isResizable = input<boolean>(true, { alias: 'isResizable' });

  public readonly $defaultSort = input<SortConfig[]>([DefaultGlobalSort], { alias: 'defaultSort' });
  public readonly $defaultFilter = input<CompositeFilterDescriptor | undefined>(undefined, { alias: 'defaultFilter' });
  public readonly $defaultPageSize = input<number>(DefaultPageSize, { alias: 'defaultPageSize' });

  /// Outputs
  public readonly cellClickEvent = output<CellClickEvent>();
  public readonly dataQueryEvent = output<GridifyQuery>();

  public constructor() {
    effect(() => {
      const gridDataQuery = this.$gridDataQuery();
      const gridifyQuery: GridifyQuery = this.gridService.buildQuery(gridDataQuery);
      this.dataQueryEvent.emit(gridifyQuery);
    });
  }

  private buildFilter(searchValue: string): CompositeFilterDescriptor {
    const terms = searchValue.split(' ');
    const columns = this.$columnsReference();
    const filterableColumns = columns.filter(c => c.filterable && c.field && c.filter === 'text');

    return {
      logic: 'and',
      filters: terms.map(term => ({
        logic: 'or',
        filters: filterableColumns.map(e => ({
          field: e.field,
          operator: 'contains',
          value: term,
        })),
      })),
    };
  }

  private resetColumns() {
    const grid = this.$gridReference();
    const columns = this.$columnsReference();

    // A [width] is required on all grid-columns for responsiveMode to do anything.
    // Undocumented, too! Thanks Telerik!
    const arr = columns.map(col => {
      col.width ??= 200;
      return col;
    });
    grid.columns.reset(arr, v => v.title);
  }

  protected onStartSearch = () => {
    this.$isSearchLoading.set(true);
  };

  protected onSearch = () => {
    const filter = this.searchValue ? this.buildFilter(this.searchValue) : undefined;
    return this.$gridDataQuery.update(prev => ({
      ...prev,
      filter,
      skip: 0,
    }));
  };
  protected onCellClick = (e: CellClickEvent) => this.cellClickEvent.emit(e);

  protected onDataStateChange = (e: DataStateChangeEvent) => {
    this.$gridDataQuery.set(e);
  };

  public ngAfterContentChecked() {
    // This forces the grid to reload its columns on changes, which is a thing it apparently doesn't do on its own.
    this.resetColumns();
  }

  public ngAfterContentInit() {
    // This is a failsafe to make sure the config gets loaded at least once (required for some grids)
    this.resetColumns();
  }
}
